// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { MachineModel } from '../../../../app/models/machine';
import { firstValueFrom } from 'rxjs';
import { BufferstockModel } from 'src/packages/warehouse';
import { Adapter } from './adapter';
import { BookingPosition } from '../../types';
import { Log } from '../../util/log';

/**
 * Liest Maschinen und deren Lager (StockObject/Bufferstock) im lokalen Bestand
 * und delegiert Bestandsänderungen an den StockObjectAdapter.
 */
export class MachineAdapter extends Adapter {
  /**
   * Lädt eine Maschine aus dem lokalen Bestand.
   *
   * @param id Maschinen-ID
   * @returns Die Maschine oder `null`, wenn sie fehlt oder nicht lesbar ist.
   */
  public async findLocal(id: number): Promise<MachineModel | null> {
    try {
      const result = await firstValueFrom(
        this.services.machineService.find(id),
      );
      return result ?? null;
    } catch (error) {
      Log.error('MachineAdapter.findLocal', error, { id });
      return null;
    }
  }

  /**
   * Lädt den Bufferstock, der der Maschine über ihren Standort zugeordnet ist.
   *
   * @param machineId Maschinen-ID
   * @returns Der zugeordnete Bufferstock oder `null`, wenn Maschine/Zuordnung fehlen.
   */
  public async findBufferstockLocal(
    machineId: number,
  ): Promise<BufferstockModel | null> {
    const machine = await this.findLocal(machineId);
    const bufferstockId =
      machine?.customer_location_bufferstock?.stock_object?.id;
    if (!bufferstockId) {
      return null;
    }
    return await this.adapters.bufferstock.findLocal(bufferstockId);
  }

  /**
   * Ermittelt die StockObject-ID des Maschinenlagers (Standort der Maschine).
   *
   * @param machineId Maschinen-ID
   * @returns Die StockObject-ID oder `null`, wenn Maschine/Lager fehlen.
   */
  public async findStockObjectIdLocal(
    machineId: number,
  ): Promise<number | null> {
    const machine = await this.findLocal(machineId);
    return machine?.customer_location?.stock_object?.id ?? null;
  }

  /**
   * Lokal gebuchte Menge eines Artikels in einer Maschine (StockObject).
   *
   * @param machineId Machine-ID
   * @param articleId Artikel-ID
   * @returns Die Menge, oder 0, wenn Position oder Maschine->StockObject fehlen.
   */
  public async getArticleAmountLocal(
    machineId: number,
    articleId: number,
  ): Promise<number> {
    const stockObjectId = await this.findStockObjectIdLocal(machineId);
    return stockObjectId === null
      ? 0
      : this.adapters.stockObject.getArticleAmountLocal(
          stockObjectId,
          articleId,
        );
  }

  /**
   * Bucht `delta` auf das Maschinenlager (delegiert an den StockObjectAdapter).
   *
   * @param position Buchungsposition (liefert die Artikel-ID)
   * @param machineId Maschine, deren Lager gebucht wird
   * @param delta Bestandsänderung (negativ = Abgang)
   * @returns `false`, wenn die Maschine kein Lager hat oder das Speichern fehlschlägt.
   */
  public async updatePositionAmountLocal(
    position: BookingPosition,
    machineId: number,
    delta: number,
  ): Promise<boolean> {
    const stockObjectId = await this.findStockObjectIdLocal(machineId);
    if (stockObjectId === null) {
      return false;
    }
    return this.adapters.stockObject.updatePositionAmountLocal(
      position,
      stockObjectId,
      delta,
    );
  }
}